import * as XLSX from 'xlsx';
import { combinedCauseNumber } from './rosterCaseCause';

const nonExportableField = (field) => field === 'save' || field.endsWith('_action');

export function exportableRosterColumns(columns, visibilityModel = {}, hiddenFields = new Set()) {
  return columns.filter((column) => (
    column.field
    && !nonExportableField(column.field)
    && !hiddenFields.has(column.field)
    && visibilityModel[column.field] !== false
  ));
}

function valueForExcelColumn(column, row) {
  if (column.field === 'cause_number') return combinedCauseNumber(row);
  if (column.field === 'ssn_dl') return row.driver_number || '';
  if (column.field.startsWith('attendance_')) {
    const day = column.field.slice('attendance_'.length);
    return row.attendance?.[day] ? 'Present' : 'Absent';
  }
  if (column.field === 'certificate_status') {
    return row.certificate_printed
      ? `Printed${row.certificate_print_count ? ` x${row.certificate_print_count}` : ''}`
      : 'Not printed';
  }
  if (column.valueGetter) return column.valueGetter(undefined, row) ?? '';
  return row[column.field] ?? '';
}

export function rosterExcelRows(columns, rows) {
  return rows.map((row) => columns.map((column) => valueForExcelColumn(column, row)));
}

function isDwiInterventionClass(selectedClass = {}) {
  const code = String(selectedClass.class_code || '').trim().toUpperCase().replace(/[^A-Z0-9]/g, '');
  const haystack = `${selectedClass.class_name || ''} ${selectedClass.class_description || ''}`.toUpperCase();
  return code === '11'
    || code === 'DWII'
    || haystack.includes('DWI INTV')
    || haystack.includes('DWI INTERVENTION')
    || haystack.includes('REPEAT OFFENDER');
}

export function dwiInterventionAttendanceColumns(selectedClass, columns, attendanceDays = []) {
  if (!isDwiInterventionClass(selectedClass)) return columns;

  const isAttendanceColumn = (column) => column.field?.startsWith('attendance_')
    || column.field?.startsWith('dwi_attendance_slot_');
  const firstAttendanceIndex = columns.findIndex(isAttendanceColumn);
  const columnsWithoutAttendance = columns.filter((column) => !isAttendanceColumn(column));
  const fallbackIndex = columnsWithoutAttendance.findIndex((column) => column.field === 'birth_date');
  const insertionIndex = firstAttendanceIndex >= 0
    ? firstAttendanceIndex
    : (fallbackIndex >= 0 ? fallbackIndex + 1 : columnsWithoutAttendance.length);
  const attendanceColumns = Array.from({ length: 10 }, (_, index) => {
    const day = attendanceDays[index] || '';
    return {
      field: `dwi_attendance_slot_${index + 1}`,
      headerName: `#${index + 1}`,
      valueGetter: (_, row = {}) => {
        if (!day) return '';
        return row.attendance?.[day] ? 'Present' : 'Absent';
      },
    };
  });

  return [
    ...columnsWithoutAttendance.slice(0, insertionIndex),
    ...attendanceColumns,
    ...columnsWithoutAttendance.slice(insertionIndex),
  ];
}

export function buildRosterWorkbook(selectedClass, columns, rows) {
  const classLabel = [selectedClass.class_code, selectedClass.class_name].filter(Boolean).join(' — ');
  const instructor = selectedClass.instructor_display || selectedClass.instructor_name || selectedClass.instructor || '';
  const dataRows = rosterExcelRows(columns, rows);
  const worksheet = XLSX.utils.aoa_to_sheet([
    ['Class', classLabel],
    ['Class Date', '____________________'],
    ['Class #', '____________________'],
    ['Instructor', instructor],
    [],
    columns.map((column) => column.headerName || column.field),
    ...dataRows
  ]);
  worksheet['!cols'] = columns.map((column, columnIndex) => {
    const header = column.headerName || column.field;
    const contentWidth = Math.max(
      String(header).length,
      ...dataRows.map((row) => String(row[columnIndex] ?? '').length)
    ) + 2;
    const minimumWidth = column.field === 'student_name' ? 32 : 12;
    const maximumWidth = column.field === 'student_name' ? 48 : 32;
    return { wch: Math.min(Math.max(contentWidth, minimumWidth), maximumWidth) };
  });

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Roster');
  return workbook;
}

export function downloadRosterWorkbook(workbook, filename) {
  XLSX.writeFile(workbook, filename, { compression: true });
}
